import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';

import { ReadOnlyPdfReviewComponent } from '../../components/read-only-pdf-review/read-only-pdf-review.component';
import {
  DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS,
  type BrandSignatureFrameSettings,
} from '../../models/brand.models';
import type { DocumentFieldDraft, DocumentSummary, PublicReviewSessionResponse } from '../../models/document.models';
import { BrandApiService } from '../../services/brand-api.service';
import { DocumentApiService } from '../../services/document-api.service';
import { GlobalFeedbackService } from '../../services/global-feedback.service';
import { UserDateTimeFormatService } from '../../services/user-date-time-format.service';
import { normalizeApiError } from '../../utils/error-normalizer';

interface DocumentsListQueryState {
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}

interface ReadonlyDocumentViewData {
  id: string;
  title: string;
  status: string;
  pageCount: number;
  fields: DocumentFieldDraft[];
  recipients: Array<{
    id: string;
    roleId?: string;
    name?: string;
    email?: string;
    status: string;
    completedAt?: string;
  }>;
  sourceFileId?: string;
}

@Component({
  selector: 'app-document-readonly-view',
  imports: [CommonModule, ReadOnlyPdfReviewComponent],
  templateUrl: './document-readonly-view.component.html',
  styleUrl: './document-readonly-view.component.scss',
})
export class DocumentReadonlyViewComponent implements OnInit, OnDestroy {
  document: ReadonlyDocumentViewData | null = null;
  loading = false;
  pdfLoading = false;
  errorMessage = '';
  pdfUrl: string | null = null;
  brandSignatureFrame: BrandSignatureFrameSettings = DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS;
  signatureFrameMetadata: Record<string, { name: string; email: string; timestampLabel: string; documentId: string }> = {};

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly brandApi = inject(BrandApiService);
  private readonly api = inject(DocumentApiService);
  private readonly feedback = inject(GlobalFeedbackService);
  private readonly dateTimeFormat = inject(UserDateTimeFormatService);
  private readonly cdr = inject(ChangeDetectorRef);

  private fieldValuesValue = new Map<string, unknown>();

  ngOnInit(): void {
    const reviewToken = this.route.snapshot.paramMap.get('token');
    if (reviewToken) {
      this.loadPublicReview(reviewToken);
      return;
    }

    const documentId = this.route.snapshot.paramMap.get('id');
    if (!documentId) {
      this.errorMessage = 'Document identifier is missing.';
      return;
    }

    this.loadDocument(documentId);
  }

  ngOnDestroy(): void {
    this.releasePdfUrl();
  }

  get reviewMetaItems(): Array<{ label: string; value: string }> {
    const document = this.document;
    if (!document) {
      return [];
    }

    const completedRecipients = document.recipients.filter((recipient) => recipient.status === 'completed').length;

    return [
      { label: 'Document', value: document.title },
      { label: 'Status', value: document.status.replaceAll('_', ' ') },
      { label: 'Recipients', value: `${completedRecipients}/${document.recipients.length} completed` },
      { label: 'Pages', value: String(document.pageCount) },
    ];
  }

  get reviewCopy(): string {
    const document = this.document;
    if (!document) {
      return '';
    }

    if (this.isPublicMode) {
      return `This shared copy is read-only. You can review the document fields and submitted values for ${document.title} below.`;
    }

    return `This sender view is read-only. You can review the document fields and submitted values for ${document.title} below.`;
  }

  get fieldValues(): ReadonlyMap<string, unknown> {
    return this.fieldValuesValue;
  }

  get isPublicMode(): boolean {
    return !!this.route.snapshot.paramMap.get('token');
  }

  get listQueryParams(): DocumentsListQueryState {
    const params = this.route.snapshot.queryParamMap;
    const state: DocumentsListQueryState = {};
    const status = params.get('status');
    const search = params.get('search');
    const page = this.parsePositiveInteger(params.get('page'));
    const limit = this.parsePositiveInteger(params.get('limit'));

    if (status) {
      state.status = status;
    }

    if (search) {
      state.search = search;
    }

    if (page) {
      state.page = page;
    }

    if (limit) {
      state.limit = limit;
    }

    return state;
  }

  navigateBackToList(): void {
    void this.router.navigate(['/documents'], {
      queryParams: this.listQueryParams,
    });
  }

  retry(): void {
    const reviewToken = this.route.snapshot.paramMap.get('token');
    if (reviewToken) {
      this.loadPublicReview(reviewToken);
      return;
    }

    const documentId = this.route.snapshot.paramMap.get('id');
    if (!documentId) {
      this.errorMessage = 'Document identifier is missing.';
      return;
    }

    this.loadDocument(documentId);
  }

  private loadDocument(documentId: string): void {
    this.loading = true;
    this.errorMessage = '';
    this.document = null;
    this.fieldValuesValue = new Map();
    this.brandSignatureFrame = DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS;
    this.signatureFrameMetadata = {};
    this.releasePdfUrl();

    this.api
      .getDocument(documentId)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (document) => {
          this.feedback.clearError();
          this.document = {
            id: document._id,
            title: document.document.title,
            status: document.status,
            pageCount: document.sourceFile.pageCount,
            fields: document.fields,
            recipients: document.recipients,
            sourceFileId: document.sourceFile.fileId,
          };
          this.fieldValuesValue = this.mergeSubmissionValues(document);
          this.signatureFrameMetadata = this.buildSignatureFrameMetadataForDocument(document);
          this.errorMessage = '';
          this.loadPdfBlob(document.sourceFile.fileId);
          this.loadBrandSignatureFrame(document.documentSettings.brandId);
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          const normalized = normalizeApiError(error, {
            fallbackMessage: 'Failed to load document view',
          });
          this.errorMessage = normalized.message;
          this.feedback.showError({
            ...normalized,
            actionLabel: 'Retry',
            action: () => this.retry(),
          });
          this.cdr.markForCheck();
        },
      });
  }

  private loadPublicReview(token: string): void {
    this.loading = true;
    this.errorMessage = '';
    this.document = null;
    this.fieldValuesValue = new Map();
    this.brandSignatureFrame = DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS;
    this.signatureFrameMetadata = {};
    this.releasePdfUrl();

    this.api
      .getPublicReviewSession(token)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (session) => {
          this.feedback.clearError();
          this.document = {
            id: session.document.id,
            title: session.document.title,
            status: session.document.status,
            pageCount: session.document.sourceFile.pageCount,
            fields: session.fields,
            recipients: session.recipients,
          };
          this.fieldValuesValue = this.mergePublicValues(session);
          this.brandSignatureFrame = session.brandSignatureFrame ?? DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS;
          this.signatureFrameMetadata = this.buildSignatureFrameMetadataForPublicReview(session);
          this.errorMessage = '';
          this.loadPublicPdfBlob(token);
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          const normalized = normalizeApiError(error, {
            fallbackMessage: 'Failed to load shared document view',
          });
          this.errorMessage = normalized.message;
          this.feedback.showError({
            ...normalized,
            actionLabel: 'Retry',
            action: () => this.retry(),
          });
          this.cdr.markForCheck();
        },
      });
  }

  private loadPdfBlob(fileId: string): void {
    this.pdfLoading = true;

    this.api
      .downloadSourcePdf(fileId)
      .pipe(
        finalize(() => {
          this.pdfLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (blob) => {
          this.feedback.clearError();
          this.releasePdfUrl();
          this.pdfUrl = URL.createObjectURL(blob);
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.pdfUrl = null;
          this.feedback.showNormalizedError(error, {
            fallbackMessage: 'Failed to load document PDF',
          });
          this.cdr.markForCheck();
        },
      });
  }

  private loadPublicPdfBlob(token: string): void {
    this.pdfLoading = true;

    this.api
      .getPublicReviewPdf(token)
      .pipe(
        finalize(() => {
          this.pdfLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (blob) => {
          this.feedback.clearError();
          this.releasePdfUrl();
          this.pdfUrl = URL.createObjectURL(blob);
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.pdfUrl = null;
          this.feedback.showNormalizedError(error, {
            fallbackMessage: 'Failed to load document PDF',
          });
          this.cdr.markForCheck();
        },
      });
  }

  private mergeSubmissionValues(document: DocumentSummary): Map<string, unknown> {
    const values = new Map<string, unknown>();
    const submissions = [...(document.submissions ?? [])].sort(
      (left, right) => new Date(left.submittedAt).getTime() - new Date(right.submittedAt).getTime(),
    );

    for (const submission of submissions) {
      for (const value of submission.values) {
        values.set(value.fieldId, value.value);
      }
    }

    return values;
  }

  private mergePublicValues(session: PublicReviewSessionResponse): Map<string, unknown> {
    const values = new Map<string, unknown>();

    for (const value of session.values) {
      values.set(value.fieldId, value.value);
    }

    return values;
  }

  private loadBrandSignatureFrame(brandId: string | undefined): void {
    const normalizedBrandId = brandId?.trim();
    if (!normalizedBrandId) {
      this.brandSignatureFrame = DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS;
      this.cdr.markForCheck();
      return;
    }

    this.brandApi.getBrand(normalizedBrandId).subscribe({
      next: (brand) => {
        this.brandSignatureFrame = brand.signatureFrame ?? DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS;
        this.cdr.markForCheck();
      },
      error: () => {
        this.brandSignatureFrame = DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS;
        this.cdr.markForCheck();
      },
    });
  }

  private buildSignatureFrameMetadataForDocument(
    document: DocumentSummary,
  ): Record<string, { name: string; email: string; timestampLabel: string; documentId: string }> {
    const recipientsById = new Map(document.recipients.map((recipient) => [recipient.id, recipient]));
    const valuesByFieldId = new Map<string, { name: string; email: string; timestampLabel: string; documentId: string }>();
    const submissions = [...(document.submissions ?? [])].sort(
      (left, right) => new Date(left.submittedAt).getTime() - new Date(right.submittedAt).getTime(),
    );

    for (const submission of submissions) {
      const recipient = recipientsById.get(submission.recipientId);
      const timestampLabel = this.formatTimestampLabel(submission.submittedAt);
      for (const value of submission.values) {
        valuesByFieldId.set(value.fieldId, {
          name: recipient?.name ?? '',
          email: recipient?.email ?? '',
          timestampLabel,
          documentId: document._id,
        });
      }
    }

    return Object.fromEntries(
      document.fields
        .filter((field) => field.type === 'signature')
        .map((field) => [
          field.id,
          valuesByFieldId.get(field.id) ?? {
            name: '',
            email: '',
            timestampLabel: '',
            documentId: document._id,
          },
        ]),
    );
  }

  private buildSignatureFrameMetadataForPublicReview(
    session: PublicReviewSessionResponse,
  ): Record<string, { name: string; email: string; timestampLabel: string; documentId: string }> {
    const recipientsByRoleId = new Map(session.recipients.map((recipient) => [recipient.roleId, recipient]));
    return Object.fromEntries(
      session.fields
        .filter((field) => field.type === 'signature')
        .map((field) => {
          const recipient = recipientsByRoleId.get(field.role);
          return [
            field.id,
            {
              name: recipient?.name ?? '',
              email: '',
              timestampLabel: this.formatTimestampLabel(recipient?.completedAt),
              documentId: session.document.id,
            },
          ];
        }),
    );
  }

  private formatTimestampLabel(timestampIso: string | undefined): string {
    if (!timestampIso) {
      return '';
    }

    return this.dateTimeFormat.formatDateTime(timestampIso);
  }

  private releasePdfUrl(): void {
    if (!this.pdfUrl) {
      return;
    }

    URL.revokeObjectURL(this.pdfUrl);
    this.pdfUrl = null;
  }

  private parsePositiveInteger(value: string | null): number | undefined {
    if (!value) {
      return undefined;
    }

    const parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
  }
}
